import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import FilterDropdown, { FilterSection } from '../../../components/FilterDropdown';
import ActiveFilterChips, { FilterChip } from '../../../components/ActiveFilterChips';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronUpDownIcon,
} from '@heroicons/react/24/outline';
import {
  CAB_TYPE_OPTIONS,
  fetchCabDefinitions,
} from '../../../services/cabDefinitionsService';
import type { CabDefinition } from '../../../types/cabDefinition';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

type SortableColumn = 'name' | 'cabType' | 'cabManagerName';

const COLUMN_FILTERS = ['name', 'cabType', 'cabManager'] as const;
type ColumnFilterKey = (typeof COLUMN_FILTERS)[number];

const filterSections: FilterSection[] = [
  {
    id: 'cabType',
    label: 'CAB Type',
    type: 'multi-select',
    options: CAB_TYPE_OPTIONS,
  },
];

const CabDefinitionsList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const patchSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const page = useMemo(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(p) && p >= 1 ? p : 1;
  }, [searchParams]);

  const pageSize = useMemo(() => {
    const ps = parseInt(searchParams.get('pageSize') || '25', 10);
    return PAGE_SIZE_OPTIONS.includes(ps) ? ps : 25;
  }, [searchParams]);

  const appliedSearchTerm = searchParams.get('search') ?? '';
  const filterCabType = useMemo(
    () => (searchParams.get('cabType') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const sortBy = (searchParams.get('sortBy') as SortableColumn) || 'name';
  const sortOrder = searchParams.get('sortOrder') === 'desc' ? 'desc' : 'asc';

  const columnFilters = useMemo(
    () => ({
      name: searchParams.get('filterName') ?? '',
      cabType: searchParams.get('filterCabType') ?? '',
      cabManager: searchParams.get('filterCabManager') ?? '',
    }),
    [searchParams]
  );

  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  const [columnFilterInputs, setColumnFilterInputs] = useState(columnFilters);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);

  useEffect(() => {
    setSearchInput(appliedSearchTerm);
  }, [appliedSearchTerm]);

  useEffect(() => {
    setColumnFilterInputs(columnFilters);
  }, [columnFilters]);

  const cabTypeFilterValue = filterCabType.length === 1 ? filterCabType[0] : undefined;

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [
      'cab-definitions',
      page,
      pageSize,
      appliedSearchTerm,
      columnFilters,
      filterCabType,
      sortBy,
      sortOrder,
    ],
    queryFn: () =>
      fetchCabDefinitions({
        page,
        pageSize,
        search: appliedSearchTerm || undefined,
        name: columnFilters.name || undefined,
        cabType: columnFilters.cabType || cabTypeFilterValue,
        cabManager: columnFilters.cabManager || undefined,
        sortBy,
        sortOrder,
      }),
    staleTime: 30000,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    patchSearchParams({ search: searchInput.trim() || null, page: '1' });
  };

  const handleFilterChange = (sectionId: string, value: string | string[]) => {
    if (sectionId === 'cabType') {
      const arr = value as string[];
      patchSearchParams({
        cabType: arr.length > 0 ? arr.join(',') : null,
        page: '1',
      });
    }
  };

  const handleColumnFilterApply = (key: ColumnFilterKey) => {
    const paramKey =
      key === 'name'
        ? 'filterName'
        : key === 'cabType'
          ? 'filterCabType'
          : 'filterCabManager';
    patchSearchParams({
      [paramKey]: columnFilterInputs[key].trim() || null,
      page: '1',
    });
  };

  const handleColumnFilterKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    key: ColumnFilterKey
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleColumnFilterApply(key);
    }
  };

  const handleSort = (column: SortableColumn) => {
    if (sortBy === column) {
      patchSearchParams({
        sortOrder: sortOrder === 'asc' ? 'desc' : 'asc',
        page: '1',
      });
    } else {
      patchSearchParams({ sortBy: column, sortOrder: 'asc', page: '1' });
    }
  };

  const renderSortIcon = (column: SortableColumn) => {
    if (sortBy !== column) {
      return <ChevronUpDownIcon className="h-3.5 w-3.5 text-gray-400" />;
    }
    return sortOrder === 'asc' ? (
      <ChevronUpIcon className="h-3.5 w-3.5 text-indigo-600" />
    ) : (
      <ChevronDownIcon className="h-3.5 w-3.5 text-indigo-600" />
    );
  };

  const activeFilterChips = useMemo((): FilterChip[] => {
    const chips: FilterChip[] = [];
    if (appliedSearchTerm) {
      chips.push({ id: 'search', label: 'Search', value: `"${appliedSearchTerm}"`, color: 'blue' });
    }
    if (filterCabType.length > 0) {
      chips.push({
        id: 'cabType',
        label: 'CAB Type',
        value: filterCabType.join(', '),
        color: 'purple',
      });
    }
    if (columnFilters.name) {
      chips.push({ id: 'filterName', label: 'Name', value: columnFilters.name, color: 'teal' });
    }
    if (columnFilters.cabType) {
      chips.push({ id: 'filterCabType', label: 'CAB Type', value: columnFilters.cabType, color: 'teal' });
    }
    if (columnFilters.cabManager) {
      chips.push({
        id: 'filterCabManager',
        label: 'CAB Manager',
        value: columnFilters.cabManager,
        color: 'teal',
      });
    }
    return chips;
  }, [appliedSearchTerm, filterCabType, columnFilters]);

  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'search':
        setSearchInput('');
        patchSearchParams({ search: null, page: '1' });
        break;
      case 'cabType':
        patchSearchParams({ cabType: null, page: '1' });
        break;
      case 'filterName':
        setColumnFilterInputs((prev) => ({ ...prev, name: '' }));
        patchSearchParams({ filterName: null, page: '1' });
        break;
      case 'filterCabType':
        setColumnFilterInputs((prev) => ({ ...prev, cabType: '' }));
        patchSearchParams({ filterCabType: null, page: '1' });
        break;
      case 'filterCabManager':
        setColumnFilterInputs((prev) => ({ ...prev, cabManager: '' }));
        patchSearchParams({ filterCabManager: null, page: '1' });
        break;
    }
  };

  const handleClearAllFilters = () => {
    setSearchInput('');
    setColumnFilterInputs({ name: '', cabType: '', cabManager: '' });
    patchSearchParams({
      search: null,
      cabType: null,
      filterName: null,
      filterCabType: null,
      filterCabManager: null,
      page: '1',
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(items.map((item) => item.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectRow = (id: string, checked: boolean) => {
    const next = new Set(selectedIds);
    if (checked) {
      next.add(id);
    } else {
      next.delete(id);
    }
    setSelectedIds(next);
  };

  const isAllSelected = items.length > 0 && selectedIds.size === items.length;
  const isSomeSelected = selectedIds.size > 0 && selectedIds.size < items.length;

  const handleBulkAction = (action: string) => {
    setShowActionsDropdown(false);
    toast.success(`${action} applied to ${selectedIds.size} selected definition(s) (placeholder)`);
    setSelectedIds(new Set());
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4" />
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">Error loading CAB definitions. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-hidden">
      <nav className="mb-2 flex-shrink-0 text-sm text-gray-500" aria-label="Breadcrumb">
        <ol className="flex items-center gap-1 flex-wrap">
          <li>Admin</li>
          <li>
            <ChevronRightIcon className="h-4 w-4 inline text-gray-400" aria-hidden />
          </li>
          <li>CAB Meeting Management</li>
          <li>
            <ChevronRightIcon className="h-4 w-4 inline text-gray-400" aria-hidden />
          </li>
          <li className="text-gray-900 font-medium">CAB Definitions</li>
        </ol>
      </nav>

      <div className="bg-white shadow rounded-lg mb-2 flex-shrink-0">
        <div className="px-4 py-2">
          <form onSubmit={handleSearch} className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-lg font-bold text-gray-900 whitespace-nowrap">CAB Definitions</h1>
              <FilterDropdown
                sections={filterSections}
                values={{ cabType: filterCabType }}
                onChange={handleFilterChange}
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search CAB definitions..."
                  className="pl-9 pr-3 py-1.5 w-56 sm:w-64 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <button
                type="submit"
                className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
              >
                Search
              </button>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                  disabled={selectedIds.size === 0}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Actions on selected rows
                  <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                </button>
                {showActionsDropdown && selectedIds.size > 0 && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowActionsDropdown(false)}
                    />
                    <div className="absolute right-0 mt-1 w-52 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
                      <div className="py-1">
                        <button
                          type="button"
                          onClick={() => handleBulkAction('Activate')}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          Activate
                        </button>
                        <button
                          type="button"
                          onClick={() => handleBulkAction('Deactivate')}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          Deactivate
                        </button>
                        <button
                          type="button"
                          onClick={() => handleBulkAction('Delete')}
                          className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <button
                type="button"
                onClick={() => navigate('/admin/cab-meeting-management/cab-definitions/new')}
                className="inline-flex items-center px-3 py-1.5 text-sm border border-transparent rounded-md shadow-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <PlusIcon className="-ml-0.5 mr-1 h-4 w-4" />
                New
              </button>
            </div>
          </form>
        </div>
      </div>

      {activeFilterChips.length > 0 && (
        <div className="mb-2 flex-shrink-0">
          <ActiveFilterChips
            chips={activeFilterChips}
            onRemove={handleRemoveFilterChip}
            onClearAll={handleClearAllFilters}
          />
        </div>
      )}

      {selectedIds.size > 0 && (
        <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-2 mb-2 flex-shrink-0 text-sm text-indigo-800">
          {selectedIds.size} row{selectedIds.size !== 1 ? 's' : ''} selected
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            className="ml-3 text-indigo-600 hover:text-indigo-800 underline"
          >
            Clear selection
          </button>
        </div>
      )}

      <div className="flex-1 min-h-0 flex flex-col">
        {items.length > 0 ? (
          <>
            <div className="overflow-auto flex-1" style={{ scrollbarGutter: 'stable' }}>
              <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2 w-10 bg-gray-50 border-b border-gray-200">
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        ref={(input) => {
                          if (input) input.indeterminate = isSomeSelected;
                        }}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        aria-label="Select all rows"
                      />
                    </th>
                    <th className="px-2 py-2 w-8 bg-gray-50 border-b border-gray-200">
                      <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" aria-hidden />
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                      <button
                        type="button"
                        onClick={() => handleSort('name')}
                        className="inline-flex items-center gap-1 hover:text-gray-700"
                      >
                        Name
                        {renderSortIcon('name')}
                      </button>
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                      <button
                        type="button"
                        onClick={() => handleSort('cabType')}
                        className="inline-flex items-center gap-1 hover:text-gray-700"
                      >
                        CAB Type
                        {renderSortIcon('cabType')}
                      </button>
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                      <button
                        type="button"
                        onClick={() => handleSort('cabManagerName')}
                        className="inline-flex items-center gap-1 hover:text-gray-700"
                      >
                        CAB Manager
                        {renderSortIcon('cabManagerName')}
                      </button>
                    </th>
                  </tr>
                  <tr className="bg-gray-50">
                    <th className="px-3 py-1 bg-gray-50" />
                    <th className="px-2 py-1 bg-gray-50" />
                    {COLUMN_FILTERS.map((key) => (
                      <th key={key} className="px-2 py-1 bg-gray-50">
                        <input
                          type="text"
                          value={columnFilterInputs[key]}
                          onChange={(e) =>
                            setColumnFilterInputs((prev) => ({
                              ...prev,
                              [key]: e.target.value,
                            }))
                          }
                          onBlur={() => handleColumnFilterApply(key)}
                          onKeyDown={(e) => handleColumnFilterKeyDown(e, key)}
                          placeholder="Search"
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item: CabDefinition, index: number) => (
                    <tr
                      key={item.id}
                      className={index % 2 === 1 ? 'bg-gray-50/60 hover:bg-gray-100' : 'hover:bg-gray-50'}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(item.id)}
                          onChange={(e) => handleSelectRow(item.id, e.target.checked)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          aria-label={`Select ${item.name}`}
                        />
                      </td>
                      <td className="px-2 py-2" />
                      <td className="px-3 py-2 text-sm">
                        <Link
                          to={`/admin/cab-meeting-management/cab-definitions/${item.id}`}
                          className="text-indigo-600 hover:text-indigo-900 hover:underline font-medium"
                        >
                          {item.name}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-900">{item.cabType}</td>
                      <td className="px-3 py-2 text-sm">
                        <span className="text-indigo-600 hover:text-indigo-900 cursor-pointer">
                          {item.cabManagerName}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-2 flex flex-wrap items-center justify-between gap-3 bg-white px-4 py-2 rounded-lg shadow flex-shrink-0">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <span>Show</span>
                <select
                  value={pageSize}
                  onChange={(e) =>
                    patchSearchParams({ pageSize: e.target.value, page: '1' })
                  }
                  className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <span>entries</span>
                {isFetching && !isLoading && (
                  <span className="text-gray-400 text-xs ml-2">Updating...</span>
                )}
              </div>

              <div className="text-sm text-gray-700">
                Showing {total === 0 ? 0 : (page - 1) * pageSize + 1} to{' '}
                {Math.min(page * pageSize, total)} of {total} entries
              </div>

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => patchSearchParams({ page: '1' })}
                  disabled={page === 1}
                  className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  &laquo;
                </button>
                <button
                  type="button"
                  onClick={() => patchSearchParams({ page: String(page - 1) })}
                  disabled={page === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                {(() => {
                  const pages: (number | string)[] = [];
                  if (totalPages <= 7) {
                    for (let i = 1; i <= totalPages; i++) pages.push(i);
                  } else if (page <= 3) {
                    pages.push(1, 2, 3, 4, '...', totalPages);
                  } else if (page >= totalPages - 2) {
                    pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                  } else {
                    pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
                  }
                  return pages.map((p, idx) =>
                    p === '...' ? (
                      <span key={`ellipsis-${idx}`} className="px-2 text-sm text-gray-500">
                        ...
                      </span>
                    ) : (
                      <button
                        key={p}
                        type="button"
                        onClick={() => patchSearchParams({ page: String(p) })}
                        className={`px-3 py-1 text-sm border rounded-md ${
                          page === p
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {p}
                      </button>
                    )
                  );
                })()}
                <button
                  type="button"
                  onClick={() => patchSearchParams({ page: String(page + 1) })}
                  disabled={page >= totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
                <button
                  type="button"
                  onClick={() => patchSearchParams({ page: String(totalPages) })}
                  disabled={page >= totalPages}
                  className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  &raquo;
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-white rounded-lg shadow">
            <p className="text-gray-500">No CAB definitions found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CabDefinitionsList;
